/**
 * Client-side crash reporting for the app-wide error boundary.
 *
 * The site is a static export with no server of its own, so the browser posts
 * crash reports straight to Strapi's public `POST /error-logs` endpoint — the
 * same pattern as the enquiry forms in `service.ts`.
 *
 * The report is built to be RESOLVED, not merely counted. Everything an agent
 * (or a person) needs to trace the failure travels with it:
 *   - the raw minified stack, plus `releaseSha`/`buildId` so `yarn error:report`
 *     can symbolicate it against that build's private source maps and inline the
 *     original source from `git show <sha>:<file>`
 *   - the route, the routes before it, and a breadcrumb trail of what the user
 *     did (see `breadcrumbs.ts`)
 *   - the conditions needed to reproduce: viewport, DPR, language, timing
 *
 * Two rules this module must never break:
 *   - It cannot throw. It runs from inside an error boundary; an exception here
 *     would be caught by that same boundary and loop.
 *   - It cannot block. Reporting is fire-and-forget (`keepalive`, so the request
 *     survives the user navigating away from the broken page).
 *
 * Nothing is sent outside a production bundle, matching how `ErrorBoundary`
 * already logs to the console in development instead.
 */

import type { Breadcrumb } from '@/utils/breadcrumbs'

import { addBreadcrumb, getBreadcrumbs } from '@/utils/breadcrumbs'

export type ErrorReport = {
  message: string
  errorName?: string
  stack?: string
  /** Next.js attaches this to server-render errors; it ties back to the build log. */
  digest?: string
  /** One level of `error.cause`, if the thrower chained one. */
  cause?: string
  /**
   * React minifies its own error messages in production down to a code. The
   * decoded text lives at this URL, and it is usually the whole diagnosis —
   * #418/#423/#425 are all hydration mismatches, this repo's most common class.
   */
  reactErrorUrl?: string
  path: string
  previousPath?: string
  releaseSha?: string
  buildId?: string
  breadcrumbs: Breadcrumb[]
  context: {
    viewport: string
    devicePixelRatio: number
    language: string
    readyState: string
    msSincePageLoad: number
    routeHistory: string[]
  }
}

/** `fetch(keepalive)` caps the body at 64KB; stay clear of the edge. */
const MAX_PAYLOAD_BYTES = 48 * 1024
const MAX_STACK_LENGTH = 8000
const MAX_ROUTE_HISTORY = 10

/**
 * Routes visited this page view. Recorded here rather than read from
 * `document.referrer` at crash time, because the referrer does not update on
 * client-side navigation — on an SPA route change it still names whatever page
 * first loaded the document.
 */
const routeHistory: string[] = []

/** Crashes already reported in this page view, keyed by message + stack. */
const reported = new Set<string>()

/**
 * Called on every route change by `usePageTracking`. Repeated calls with the
 * same pathname are ignored so a re-render cannot shift the history.
 */
export function recordPathChange(pathname: string): void {
  if (routeHistory.at(-1) === pathname) return

  routeHistory.push(pathname)
  if (routeHistory.length > MAX_ROUTE_HISTORY) routeHistory.shift()

  addBreadcrumb('navigation', pathname)
}

/** Test seam — clears the module-level route history and dedupe set. */
export function resetErrorLog(): void {
  routeHistory.length = 0
  reported.clear()
}

function describeCause(error: unknown): string | undefined {
  const cause = error instanceof Error ? error.cause : undefined
  if (cause === undefined || cause === null) return undefined

  return (cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)).slice(0, 500)
}

/** Turns `Minified React error #418; visit ...` into the docs URL for that code. */
function reactErrorUrl(message: string): string | undefined {
  const code = /Minified React error #(\d+)/.exec(message)?.[1]
  return code ? `https://react.dev/errors/${code}` : undefined
}

/** Normalizes an unknown thrown value into the payload the CMS stores. */
export function buildErrorReport(error: unknown, path: string): ErrorReport {
  const isError = error instanceof Error
  const message = isError ? error.message : String(error)

  return {
    message,
    errorName: isError ? error.name : undefined,
    stack: isError ? error.stack?.slice(0, MAX_STACK_LENGTH) : undefined,
    digest: isError ? (error as Error & { digest?: string }).digest : undefined,
    cause: describeCause(error),
    reactErrorUrl: reactErrorUrl(message),
    path,
    // The route before the crash; `at(-1)` is the crashing route itself.
    previousPath: routeHistory.at(-2),
    releaseSha: process.env.NEXT_PUBLIC_RELEASE_SHA,
    buildId: process.env.NEXT_PUBLIC_BUILD_ID,
    breadcrumbs: getBreadcrumbs(),
    context: {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      language: window.navigator.language,
      readyState: document.readyState,
      msSincePageLoad: Math.round(performance.now()),
      routeHistory: [...routeHistory]
    }
  }
}

/**
 * Byte length of the serialized payload. `String.length` counts UTF-16 code
 * units, not bytes — Malay/Arabic copy in a breadcrumb detail or an error
 * message is 2–4 bytes per character, so measuring by `.length` can let a
 * report sail past `fetch(keepalive)`'s hard 64KB body cap and be dropped.
 */
function payloadBytes(report: ErrorReport): number {
  const json = JSON.stringify(report)
  return typeof TextEncoder === 'undefined'
    ? json.length * 3
    : new TextEncoder().encode(json).length
}

/**
 * Shrinks an over-large report rather than dropping it. Breadcrumbs go first
 * (oldest are least relevant), then the tail of the stack — the top frames name
 * the failure, so they are the last thing worth losing.
 */
export function fitToPayloadLimit(report: ErrorReport): ErrorReport {
  let candidate = report

  while (payloadBytes(candidate) > MAX_PAYLOAD_BYTES && candidate.breadcrumbs.length > 0) {
    candidate = { ...candidate, breadcrumbs: candidate.breadcrumbs.slice(1) }
  }

  // Trimming by characters against a byte overflow under-shoots on multi-byte
  // text, so re-measure and keep cutting instead of trusting one subtraction.
  while (payloadBytes(candidate) > MAX_PAYLOAD_BYTES && candidate.stack) {
    const overflow = payloadBytes(candidate) - MAX_PAYLOAD_BYTES
    const keep = Math.max(0, candidate.stack.length - Math.ceil(overflow / 3) - 32)
    if (keep === 0) {
      candidate = { ...candidate, stack: undefined }
      break
    }
    candidate = { ...candidate, stack: candidate.stack.slice(0, keep) }
  }

  return candidate
}

/**
 * Fire-and-forget POST. `keepalive` lets the request outlive the page, so a
 * report still lands if the user leaves the broken route immediately.
 */
function send(url: string, report: ErrorReport): void {
  fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report)
  }).catch(() => {
    // The page is already broken; a failed report must stay silent.
  })
}

/**
 * Reports one crash. Safe to call more than once for the same error — the
 * boundary's fallback re-renders on every `retry()`, and React StrictMode
 * double-invokes effects in development.
 */
export function reportError(error: unknown): void {
  try {
    if (typeof window === 'undefined') return
    if (process.env.NODE_ENV !== 'production') return

    // Checked before the report is deduped: marking a crash as reported and
    // only then discovering there is no transport would suppress it for the
    // rest of the page view.
    if (typeof fetch !== 'function') return

    const base = (process.env.NEXT_PUBLIC_CMS_BASE_URL ?? '').replace(/\/+$/, '')
    if (!base) return

    const report = buildErrorReport(error, window.location.pathname)
    const key = `${report.message}\n${report.stack ?? ''}`
    if (reported.has(key)) return
    reported.add(key)

    send(`${base}/error-logs`, fitToPayloadLimit(report))
  } catch {
    // Never let the reporter become the error.
  }
}
